import React, { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LucideHome } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

export interface BreadcrumbItemType {
  label: string;
  path: string;
  isId?: boolean;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItemType[];
  className?: string;
}

export const Breadcrumbs: FC<BreadcrumbsProps> = ({ items, className = '' }) => {
  const location = useLocation();

  // If no items are provided, generate them from the current path
  const breadcrumbItems = items || generateBreadcrumbsFromPath(location.pathname);

  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/" className="flex items-center">
              <LucideHome className="h-4 w-4" />
              <span className="sr-only">Home</span>
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />

        {breadcrumbItems.slice(1).map((item, index, array) => (
          <React.Fragment key={item.path}>
            {index === array.length - 1 ? (
              <BreadcrumbItem>
                <BreadcrumbPage className={item.isId ? 'text-xs opacity-75' : ''}>
                  {item.label}
                </BreadcrumbPage>
              </BreadcrumbItem>
            ) : (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={item.path} className={item.isId ? 'text-xs opacity-75' : ''}>
                      {item.label}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

// Helper function to generate breadcrumb items from a URL path
const generateBreadcrumbsFromPath = (path: string): BreadcrumbItemType[] => {
  // Remove trailing slash if present
  const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;

  // Split the path into segments
  const segments = normalizedPath.split('/').filter(Boolean);

  // Create breadcrumb items
  const breadcrumbs: BreadcrumbItemType[] = [{ label: 'Home', path: '/' }];

  let currentPath = '';

  segments.forEach((segment) => {
    currentPath += `/${segment}`;

    // Check if the segment looks like an ID (MongoDB ObjectId or UUID)
    const isId =
      /^[0-9a-f]{24}$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment,
      );

    // Format the label to be more readable
    let label;
    if (isId) {
      // For IDs, show a shortened version
      label = `ID: ${segment.substring(0, 8)}...`;
    } else {
      // For normal segments, capitalize and format
      label = segment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    breadcrumbs.push({
      label,
      path: currentPath,
      isId,
    });
  });

  return breadcrumbs;
};
